import { z } from "zod";

import {
  browserSafeSummarySchema,
  organizationConnectionWireSchema,
} from "@/features/settings/connectionManagementSchemas";
import { browserApiFetch } from "@/shared/api/browserApiClient";

export const storeOAuthProviders = [
  "tiktok_shop",
  "amazon_sp",
  "shopify",
  "shoplazza",
  "shopee",
] as const;

export type StoreOAuthProvider = (typeof storeOAuthProviders)[number];

export const adOAuthProviders = [
  "tiktok_business",
  "amazon_ads",
  "google_ads",
  "reddit_ads",
  "meta_ads",
] as const;

export type AdOAuthProvider = (typeof adOAuthProviders)[number];

export const communicationOAuthProviders = ["gmail", "threads", "alibaba_1688"] as const;

export type CommunicationOAuthProvider =
  (typeof communicationOAuthProviders)[number];
export type ConnectionOAuthProvider =
  | StoreOAuthProvider
  | AdOAuthProvider
  | CommunicationOAuthProvider;

const operationStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
  "canceled",
]);

const connectionOperationSchema = z
  .object({
    connection_id: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
    error_code: z.string(),
    error_message: z.string(),
    id: z.string().min(1),
    idempotency_key: z.string().min(1),
    operation_type: z.enum([
      "bind",
      "oauth_callback",
      "sync",
      "refresh",
      "disconnect",
    ]),
    organization_id: z.string().min(1),
    provider: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    requested_by_user_id: z.string().min(1),
    resource_type: z.enum([
      "shop",
      "ad_account",
      "email_account",
      "oauth_profile",
      "guild",
    ]),
    result_summary: browserSafeSummarySchema,
    status: operationStatusSchema,
    trigger_run_id: z.string().nullable(),
    trigger_task_id: z.string(),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

const nextActionSchema = z
  .object({
    auth_host: z.string().optional(),
    auth_url: z.string().optional(),
    operation_id: z.string().optional(),
    type: z.string(),
  })
  .strict();

const taskRunSchema = z
  .object({
    run_id: z.string(),
    task_id: z.string(),
  })
  .strict();

const connectionCreateResponseSchema = z
  .object({
    connection: organizationConnectionWireSchema,
    next_action: nextActionSchema.optional(),
    operation: connectionOperationSchema,
    task_run: taskRunSchema.optional(),
  })
  .strict();

const connectionOperationResponseSchema = z
  .object({ operation: connectionOperationSchema })
  .strict();

const oauthCallbackResponseSchema = z
  .object({
    next_action: nextActionSchema.optional(),
    operation: connectionOperationSchema.optional(),
    operation_id: z.string().optional(),
    status: z.string(),
    task_run: taskRunSchema.optional(),
  })
  .strict();

const apiErrorSchema = z.object({ error: z.string() }).strict();

export type StoreConnectionOperation = z.infer<
  typeof connectionOperationSchema
>;

interface CreateStoreOAuthInput {
  organizationId: string;
  provider: StoreOAuthProvider;
  shopDomain?: string;
}

export interface PreparedStoreOAuth {
  authorizationUrl: string;
  operationId: string;
}

export async function prepareStoreOAuthAuthorization(
  input: CreateStoreOAuthInput,
): Promise<PreparedStoreOAuth> {
  const context = connectionContext(input.provider, input.shopDomain);
  return prepareOAuthAuthorization({
    context,
    displayName: defaultConnectionDisplayName(
      input.provider,
      input.shopDomain,
    ),
    idempotencyScope: "store",
    organizationId: input.organizationId,
    provider: input.provider,
    resourceType: "shop",
  });
}

export async function prepareAdOAuthAuthorization(input: {
  organizationId: string;
  provider: AdOAuthProvider;
}): Promise<PreparedStoreOAuth> {
  return prepareOAuthAuthorization({
    displayName: defaultConnectionDisplayName(input.provider),
    idempotencyScope: "ad-account",
    organizationId: input.organizationId,
    provider: input.provider,
    resourceType: "ad_account",
  });
}

export async function prepareCommunicationOAuthAuthorization(input: {
  organizationId: string;
  provider: CommunicationOAuthProvider;
}): Promise<PreparedStoreOAuth> {
  return prepareOAuthAuthorization({
    displayName: defaultConnectionDisplayName(input.provider),
    idempotencyScope: "communication",
    organizationId: input.organizationId,
    provider: input.provider,
    resourceType:
      input.provider === "gmail" ? "email_account" : "oauth_profile",
  });
}

async function prepareOAuthAuthorization(input: {
  context?: Record<string, string>;
  displayName: string;
  idempotencyScope: string;
  organizationId: string;
  provider: ConnectionOAuthProvider;
  resourceType: "ad_account" | "email_account" | "oauth_profile" | "shop";
}): Promise<PreparedStoreOAuth> {
  const response = await browserApiFetch(
    `/v1/organizations/${encodeURIComponent(input.organizationId)}/connections`,
    {
      body: JSON.stringify({
        ...(input.context ? { context: input.context } : {}),
        display_name: input.displayName,
        idempotency_key: `merchant-web:${input.idempotencyScope}:${input.provider}:${crypto.randomUUID()}`,
        provider: input.provider,
        resource_type: input.resourceType,
      }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
  );
  const payload = connectionCreateResponseSchema.parse(
    await readAPIResponse(response),
  );
  assertPreparedAuthorizationEnvelope(payload, input);
  let operation = payload.operation;
  let authorizationUrl = authorizationURLFromEnvelope(payload);

  if (!authorizationUrl) {
    operation = await pollStoreConnectionOperation({
      operationId: operation.id,
      organizationId: input.organizationId,
      stopWhenAuthorizationReady: true,
    });
    assertOperationIdentity(operation, input);
    authorizationUrl = authorizationURLFromOperation(operation);
  }
  if (!authorizationUrl) {
    throw new Error(operation.error_message || "oauth_auth_url_missing");
  }
  return {
    authorizationUrl: validateAuthorizationURL(authorizationUrl),
    operationId: operation.id,
  };
}

function assertPreparedAuthorizationEnvelope(
  payload: z.infer<typeof connectionCreateResponseSchema>,
  input: {
    organizationId: string;
    provider: ConnectionOAuthProvider;
    resourceType: StoreConnectionOperation["resource_type"];
  },
) {
  if (
    payload.connection.organization_id !== input.organizationId ||
    payload.connection.provider !== input.provider ||
    payload.connection.resource_type !== input.resourceType ||
    payload.operation.connection_id !== payload.connection.id
  ) {
    throw new Error("oauth_connection_identity_mismatch");
  }
  assertOperationIdentity(payload.operation, input);
  if (
    payload.next_action?.operation_id &&
    payload.next_action.operation_id !== payload.operation.id
  ) {
    throw new Error("oauth_operation_identity_mismatch");
  }
}

function assertOperationIdentity(
  operation: StoreConnectionOperation,
  input: {
    organizationId: string;
    provider: ConnectionOAuthProvider;
    resourceType: StoreConnectionOperation["resource_type"];
  },
) {
  if (
    operation.organization_id !== input.organizationId ||
    operation.provider !== input.provider ||
    operation.resource_type !== input.resourceType
  ) {
    throw new Error("oauth_operation_identity_mismatch");
  }
}

export async function completeStoreOAuthCallback(input: {
  organizationId: string;
  provider: StoreOAuthProvider;
  search: string;
  signal?: AbortSignal;
}) {
  return completeOAuthCallback(input);
}

export async function completeAdOAuthCallback(input: {
  organizationId: string;
  provider: AdOAuthProvider;
  search: string;
  signal?: AbortSignal;
}) {
  return completeOAuthCallback(input);
}

export async function completeCommunicationOAuthCallback(input: {
  organizationId: string;
  provider: CommunicationOAuthProvider;
  search: string;
  signal?: AbortSignal;
}) {
  return completeOAuthCallback(input);
}

export async function completeSharedMetaOAuthCallback(input: {
  organizationId: string;
  search: string;
  signal?: AbortSignal;
}) {
  return completeOAuthCallback({
    ...input,
    callbackPath: "/v1/oauth/meta/callback",
    expectedProviders: ["meta_ads", "threads"],
  });
}

async function completeOAuthCallback(input: {
  callbackPath?: string;
  expectedProviders?: readonly ConnectionOAuthProvider[];
  organizationId: string;
  provider?: ConnectionOAuthProvider;
  search: string;
  signal?: AbortSignal;
}) {
  const callbackPath = input.callbackPath ?? (
    input.provider ? oauthServiceCallbackPath(input.provider) : ""
  );
  if (!callbackPath) throw new Error("oauth_callback_provider_missing");
  const response = await browserApiFetch(
    `${callbackPath}${input.search}`,
    { method: "GET", signal: input.signal },
  );
  const callback = oauthCallbackResponseSchema.parse(
    await readAPIResponse(response),
  );
  const operationId = callback.operation?.id ?? callback.operation_id;
  if (!operationId) throw new Error("oauth_callback_operation_missing");
  const operation = await pollStoreConnectionOperation({
    operationId,
    organizationId: input.organizationId,
    signal: input.signal,
  });
  const expectedProviders = input.expectedProviders ?? (
    input.provider ? [input.provider] : []
  );
  if (!expectedProviders.includes(operation.provider as ConnectionOAuthProvider)) {
    throw new Error("oauth_callback_provider_mismatch");
  }
  assertProviderResourceType(operation);
  const parentOperationId =
    normalizedString(callback.operation?.result_summary.parent_operation_id) ||
    normalizedString(operation.result_summary.parent_operation_id);
  return { ...operation, parentOperationId };
}

export async function pollStoreConnectionOperation(input: {
  onUpdate?: (operation: StoreConnectionOperation) => void;
  operationId: string;
  organizationId: string;
  signal?: AbortSignal;
  stopWhenAuthorizationReady?: boolean;
}) {
  let latest: StoreConnectionOperation | null = null;
  for (let index = 0; index < 45; index += 1) {
    const response = await browserApiFetch(
      `/v1/organizations/${encodeURIComponent(input.organizationId)}/connection-operations/${encodeURIComponent(input.operationId)}`,
      { method: "GET", signal: input.signal },
    );
    latest = connectionOperationResponseSchema.parse(
      await readAPIResponse(response),
    ).operation;
    input.onUpdate?.(latest);
    if (
      latest.status === "succeeded" ||
      latest.status === "failed" ||
      latest.status === "canceled" ||
      (input.stopWhenAuthorizationReady && authorizationURLFromOperation(latest))
    ) {
      return latest;
    }
    await abortableDelay(1_500, input.signal);
  }
  throw new Error(latest?.error_message || "operation_poll_timeout");
}

export function authorizationURLFromEnvelope(
  response: z.infer<typeof connectionCreateResponseSchema>,
) {
  return normalizedString(response.next_action?.auth_url) ||
    authorizationURLFromOperation(response.operation);
}

export function authorizationURLFromOperation(
  operation: StoreConnectionOperation,
) {
  const syncSummary = recordValue(operation.result_summary.sync_summary);
  const nextAction = recordValue(syncSummary.next_action);
  return normalizedString(nextAction.auth_url);
}

function oauthServiceCallbackPath(provider: ConnectionOAuthProvider) {
  switch (provider) {
    case "tiktok_shop":
      return "/v1/oauth/tiktok-shop/callback";
    case "amazon_sp":
      return "/v1/oauth/amazon-sp/callback";
    case "shopify":
      return "/v1/oauth/shopify/callback";
    case "shoplazza":
      return "/v1/oauth/shoplazza/callback";
    case "shopee":
      return "/v1/oauth/shopee/callback";
    case "tiktok_business":
      return "/v1/oauth/tiktok-business/callback";
    case "amazon_ads":
      return "/v1/oauth/amazon-ads/callback";
    case "google_ads":
      return "/v1/oauth/google-ads/callback";
    case "reddit_ads":
      return "/v1/oauth/reddit-ads/callback";
    case "meta_ads":
    case "threads":
      return "/v1/oauth/meta/callback";
    case "alibaba_1688":
      return "/v1/oauth/alibaba_1688/callback";
    case "gmail":
      return "/v1/oauth/gmail/callback";
  }
}

function connectionContext(
  provider: StoreOAuthProvider,
  rawShopDomain?: string,
) {
  if (provider !== "shopify" && provider !== "shoplazza") return undefined;
  const shopDomain = normalizeShopDomain(provider, rawShopDomain ?? "");
  if (!shopDomain) throw new Error("invalid_shop_domain");
  return { shop_domain: shopDomain };
}

export function normalizeShopDomain(
  provider: StoreOAuthProvider,
  value: string,
) {
  const host = value
    .trim()
    .replace(/^https?:\/\//iu, "")
    .replace(/\/.*$/u, "")
    .toLowerCase();
  if (!host || host.includes("@") || !/^[a-z0-9.-]+$/u.test(host)) return "";
  if (provider === "shopify" && !host.endsWith(".myshopify.com")) return "";
  return host;
}

function defaultConnectionDisplayName(
  provider: ConnectionOAuthProvider,
  shopDomain?: string,
) {
  const providerNames: Record<ConnectionOAuthProvider, string> = {
    alibaba_1688: "1688 采购账号",
    amazon_sp: "Amazon",
    amazon_ads: "Amazon Ads",
    google_ads: "Google Ads",
    gmail: "Gmail",
    meta_ads: "Meta Ads",
    reddit_ads: "Reddit Ads",
    shopify: "Shopify",
    shoplazza: "Shoplazza",
    shopee: "Shopee",
    tiktok_business: "TikTok Business",
    tiktok_shop: "TikTok Shop",
    threads: "Threads",
  };
  const domain = isStoreOAuthProvider(provider)
    ? normalizeShopDomain(provider, shopDomain ?? "")
    : "";
  return domain ? `${providerNames[provider]} ${domain}` : providerNames[provider];
}

function isStoreOAuthProvider(
  provider: ConnectionOAuthProvider,
): provider is StoreOAuthProvider {
  return storeOAuthProviders.some((candidate) => candidate === provider);
}

function assertProviderResourceType(operation: StoreConnectionOperation) {
  const expectedResourceTypes: Readonly<
    Partial<Record<ConnectionOAuthProvider, StoreConnectionOperation["resource_type"]>>
  > = {
    alibaba_1688: "oauth_profile",
    amazon_ads: "ad_account",
    amazon_sp: "shop",
    gmail: "email_account",
    google_ads: "ad_account",
    meta_ads: "ad_account",
    reddit_ads: "ad_account",
    shopify: "shop",
    shoplazza: "shop",
    shopee: "shop",
    threads: "oauth_profile",
    tiktok_business: "ad_account",
    tiktok_shop: "shop",
  };
  const expected = expectedResourceTypes[
    operation.provider as ConnectionOAuthProvider
  ];
  if (!expected || operation.resource_type !== expected) {
    throw new Error("oauth_callback_resource_mismatch");
  }
}

function validateAuthorizationURL(value: string) {
  const parsed = new URL(value);
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) {
    throw new Error("oauth_auth_url_untrusted");
  }
  return parsed.toString();
}

async function readAPIResponse(response: Response) {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    if (!response.ok) throw new Error(`http_${response.status}`);
  }
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    throw new Error(parsed.success ? parsed.data.error : `http_${response.status}`);
  }
  return payload;
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function normalizedString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function abortableDelay(milliseconds: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      window.clearTimeout(timer);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", abort, { once: true });
  });
}
